import type { ReactNode } from "react";
import ErrorImg from "@/assets/app-error.svg";

/**
 * Presentational full-page error state shared by the top-level ErrorBoundary
 * and the router's error component. Self-contained: no AppContext, router, or
 * network dependency, so it renders even when those layers are what failed.
 */
export function ErrorState({
  title = "Oops! Something went wrong.",
  description = "We ran into an issue while loading this page. Our team has been notified, and we're working on fixing it.",
  eventId,
  children,
}: {
  title?: string;
  description?: string;
  /** Sentry event id, surfaced so support can correlate the report. */
  eventId?: string;
  /** Action buttons. */
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-[calc(100vh-100px)] flex-col items-center justify-center gap-4 p-6 text-center">
      <img src={ErrorImg} alt="Application error" width={300} height={300} />

      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <p className="max-w-md text-sm text-muted-foreground">{description}</p>

      {children && (
        <div className="flex flex-wrap items-center justify-center gap-3">{children}</div>
      )}

      {eventId && (
        <p className="font-mono text-xs text-muted-foreground/70">Reference ID: {eventId}</p>
      )}
    </div>
  );
}
